import React, { useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
    Group,
    Text,
    AddToCollectionOutlined,
    IconButton,
    palette,
    color,
} from '@landr/maestro';
import { t, Trans } from '@lingui/macro';
import { Collection } from 'types';
import {
    CollectionsListItem,
    CollectionsListItemSkeleton,
} from 'components/CollectionsListItem';
import { noop } from 'utils/noop';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { CollectionsPerPage } from '../../../constants';
import { StaticItemsCount } from '../CollectionsList';

export const CollectionItemHeight = 60;

export interface CollectionsPageProps {
    fetchMore?: (
        options:
            | {
                  offset: number;
                  query: string;
                  take: number;
              }
            | undefined,
    ) => void;
    hide: () => void;
    items?: Collection[];
    loading: boolean;
    pageCount: number;
    pageIndex: number;
    placeholder?: string;
    sampleIds?: string[];
    searchQuery?: string;
    totalItems: number;
}

const CreateCollectionButtonText = styled(Text)`
    width: 232px;
    margin-left: 3px;
    color: ${color('text.base')};
`;

const ListItem = styled(Group.Item)`
    cursor: pointer;
    height: ${CollectionItemHeight}px;
    &:hover {
        background-color: ${({ theme }) =>
            theme.mode === 'light'
                ? palette('neutral.200')
                : palette('neutral.700')};
    }
`;

export const CollectionsPage: React.FC<CollectionsPageProps> = React.memo(
    ({
        fetchMore = noop,
        hide,
        items = [],
        loading,
        pageCount,
        pageIndex,
        placeholder,
        sampleIds,
        searchQuery,
        totalItems,
    }) => {
        const { openModal } = useModalContext();

        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pageCount - 1;

        let pageSize = isFirstPage
            ? CollectionsPerPage - StaticItemsCount
            : CollectionsPerPage;

        pageSize = isLastPage
            ? totalItems - (pageIndex * CollectionsPerPage - StaticItemsCount)
            : pageSize;

        const offset = isFirstPage
            ? 0
            : pageIndex * CollectionsPerPage - StaticItemsCount;

        useEffect(() => {
            if (!isFirstPage) {
                fetchMore({
                    variables: {
                        offset,
                        query: searchQuery,
                        take: pageSize,
                    },
                });
            }
            // Should only run when the search query changes
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchQuery]);

        const skeletons = useMemo(
            () =>
                new Array(pageSize).fill(true).map((value, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <CollectionsListItemSkeleton key={index} />
                )),
            [pageSize],
        );

        const createNewCollection = useCallback(() => {
            openModal.createCollectionModal();
            hide();
        }, [openModal, hide]);

        const createCollectionButton = useMemo(
            () => (
                <Group
                    alignItems="center"
                    gutter="sm"
                    px="lg"
                    py="sm"
                    width="100%"
                    onClick={createNewCollection}
                >
                    <Group.Item>
                        <IconButton
                            variant="subtle"
                            label={t`Create new collection`}
                            size="sm"
                            icon={AddToCollectionOutlined}
                            isRounded
                        />
                    </Group.Item>
                    <Group.Item>
                        <CreateCollectionButtonText
                            textAlign="left"
                            fontWeight="bold"
                        >
                            <Trans>Create new collection</Trans>
                        </CreateCollectionButtonText>
                    </Group.Item>
                </Group>
            ),
            [createNewCollection],
        );

        const renderedItems = items.slice(offset, offset + pageSize);

        if (loading) {
            return (
                <>
                    {isFirstPage && createCollectionButton}
                    {skeletons}
                </>
            );
        }

        return (
            <Group direction="vertical" gutter="none">
                {isFirstPage && <ListItem>{createCollectionButton}</ListItem>}
                {Object.values(renderedItems).map((item) => (
                    <ListItem key={item.guid}>
                        <CollectionsListItem
                            item={item}
                            collectionId={item.guid}
                            sampleIds={sampleIds}
                            hide={hide}
                        />
                    </ListItem>
                ))}
            </Group>
        );
    },
);

export default CollectionsPage;
